'use client';

import { useWhenCurTokenReady } from '@/hooks/useWhenReady';
import { useStore } from '@/providers/app-store-provider';
import { Drawer } from '@mui/material';
import { WapNavBackOutlined } from '@zeelool/icon';
import { useRef } from 'react';
import SearchInput from './SearchInput';
import SearchPart from './SearchPart';
import { useSearchStore } from './store/search-store';

interface SearchPopupProps {
  show: boolean;
  onClose: () => void;
}

// interface HotSearchItem {
//   name: string;
//   keywords: string;
//   products: any[];
//   jump?: {
//     id: string;
//   };
// }

interface SearchInputRef {
  clickWord: (item: any, type: string, flag: number) => void;
}

const SearchPopup = ({ show, onClose }: SearchPopupProps) => {
  const searchInputRef = useRef<SearchInputRef>(null);
  const initRef = useRef<boolean>(false);
  const isSearchDataLoading = useSearchStore((state) => state.isSearchDataLoading);
  const token = useStore((state) => state.token);
  const refreshSearchHistory = useSearchStore((state) => state.refreshSearchHistory);

  // 关闭搜索弹窗
  const handleClose = () => {
    onClose();
  };

  // 获取clickWord方法并传递给SearchPart
  const handleClickWord = (keyword: any, type: string, flag: number) => {
    if (searchInputRef.current?.clickWord) {
      searchInputRef.current.clickWord(keyword, type, flag);
    }
  };

  useWhenCurTokenReady(() => {
    // 登录态刷新的时候，重新获取历史的搜索商品数据，更新里面的价格
    refreshSearchHistory();
  }, [token]);

  return (
    <Drawer
      anchor="bottom"
      open={show}
      onClose={handleClose}
      onTransitionEnd={(event) => {
        // 当Drawer打开动画完成后，手动触发SearchInput的focus
        if (show && !initRef.current) {
          initRef.current = true;

          // 尝试多种选择器来找到input
          const container = event.currentTarget as HTMLElement;
          const input = container.querySelector('input[aria-label="Search"]') as HTMLInputElement;

          if (input) {
            setTimeout(() => {
              input.focus();
              // 移动端额外处理
              if (navigator.userAgent.match(/iPhone|iPad|iPod|Android/i)) {
                input.click();
              }
            }, 100);
          }
        }
      }}
      sx={{
        '& .MuiDrawer-paper': {
          height: '100vh',
          backgroundColor: '#F5F5F5',
          borderTopLeftRadius: '0px',
          borderTopRightRadius: '0px',
        },
      }}
      keepMounted
    >
      <div className="bg-[#f5f5f5] h-full overflow-y-auto">
        {/* 顶部搜索栏 */}
        <div className="flex items-center justify-center px-[16px] py-[20px] pb-[8px] bg-white">
          <div
            className="flex items-center justify-center w-[10%] text-center cursor-pointer"
            onClick={handleClose}
          >
            <WapNavBackOutlined className="text-[24px]" />
          </div>
          {/* 搜索输入框组件 */}
          <SearchInput ref={searchInputRef} onClose={handleClose} isShowSearchBtn />
        </div>

        {isSearchDataLoading ? (
          // 骨架屏：模拟搜索内容布局
          <div className="space-y-5 px-4">
            {/* 热词标题骨架 */}
            <div className="bg-white rounded-[20px] px-[22px] py-[32px] mt-[14px]">
              <div className="h-[24px] w-[64px] bg-gray-200 rounded-lg animate-pulse mb-8" />

              {/* 热词标签骨架 - 使用gap保持对齐一致性 */}
              <div className="flex flex-wrap gap-[12px] mb-10">
                {Array(4)
                  .fill(100)
                  .map((width, i) => (
                    <div
                      key={i}
                      className="h-[24px] bg-gray-200 rounded-lg animate-pulse"
                      style={{ width: `${width}px` }}
                    />
                  ))}
              </div>

              {/* 商品列表骨架 */}
              <div className="flex flex-wrap gap-[18px] mt-12">
                {[1, 2, 3, 4].map((item) => (
                  <div key={item} className="w-[110px]">
                    {/* 商品图片骨架 */}
                    <div className="w-[110px] h-[68px] bg-gray-200 rounded-lg animate-pulse mb-4" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* 搜索历史 */}
            <SearchPart type="history" onClose={handleClose} onClickWord={handleClickWord} />

            {/* 热门搜索 */}
            <SearchPart type="hot" onClose={handleClose} onClickWord={handleClickWord} />
          </>
        )}
        {/* 底部占位用于留一部分空间 */}
        <div className="min-h-[8vw] py-[4vw] box-content" />
      </div>
    </Drawer>
  );
};

export default SearchPopup;
